import { ReactNode } from 'react';
import { Issue } from '../../api/types/response/issue.response';
import { SortController } from 'src/types/interfaces/sort-controller';
import { SetSortController } from 'src/hooks/use-sort-controller';
import IssuesTableRow, {
  IssuesActionsSlotPayload,
} from '../molecules/issues-table-row';
import { useTranslation } from 'react-i18next';
import { TableColumn } from 'src/types/interfaces/table-column';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import { Scrollbar } from 'src/components/atoms/scrollbar';
import { DataStatusIndicator } from 'src/components/molecules/data-status-indicator/data-status-indicator';
import { SortOrder } from 'src/types/enum/sort-order';
import { handleUpdateSort } from 'src/utils/sort/handle-update-sort';

type Props = {
  data: Issue[];
  isLoading: boolean;
  error: string | undefined;
  sortController: SortController;
  setSortController: SetSortController;
  handleChangeSort: (sort: string[]) => void;
  actionsSlot: (payload: IssuesActionsSlotPayload) => ReactNode;
};

export default function IssuesTable({
  data,
  isLoading,
  error,
  sortController,
  setSortController,
  handleChangeSort,
  actionsSlot,
}: Props) {
  const { t } = useTranslation(['issues', 'common']);

  const columns: TableColumn[] = [
    {
      id: 'reportedByEmail',
      label: t('issues:common.columns.reportedByEmail'),
      isSortable: false,
    },
    {
      id: 'spaceChange',
      label: t('issues:common.columns.spaceChange'),
      isSortable: false,
    },
    {
      id: 'space',
      label: t('issues:common.columns.space'),
      isSortable: false,
    },
    {
      id: 'reportedAt',
      label: t('issues:common.columns.reportedAt'),
      isSortable: true,
    },
    {
      id: 'state',
      label: t('issues:common.columns.state'),
      isSortable: false,
    },
  ];
  return (
    <Box
      sx={{
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
      }}>
      <Scrollbar autoHide={false}>
        <Table sx={{ minWidth: 800 }}>
          <TableHead>
            <TableRow>
              {columns.map(column =>
                column.isSortable ? (
                  <TableCell key={column.id}>
                    <TableSortLabel
                      active={true}
                      direction={
                        sortController.sortBy === column.id
                          ? sortController.sortOrder
                          : SortOrder.ASC
                      }
                      disabled={isLoading}
                      onClick={() =>
                        handleUpdateSort(
                          setSortController,
                          handleChangeSort,
                          column.id,
                        )
                      }>
                      {column.label}
                    </TableSortLabel>
                  </TableCell>
                ) : (
                  <TableCell key={column.id}>{column.label}</TableCell>
                ),
              )}
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.map(
              ({
                token,
                reporterEmail,
                reportedAt,
                status,
                spaceChangeRequested,
                spaceName,
                history,
              }) => (
                <IssuesTableRow
                  key={token}
                  token={token}
                  reportedByEmail={reporterEmail}
                  reportedAt={reportedAt}
                  status={status}
                  spaceChangeRequested={spaceChangeRequested}
                  spaceName={spaceName}
                  //to be added once returned from the backend
                  spaceType={undefined}
                  actionsSlot={actionsSlot}
                  actionedByEmail={history?.[history?.length - 1]?.email || ''}
                />
              ),
            )}
          </TableBody>
        </Table>
      </Scrollbar>
      <DataStatusIndicator
        dataLength={data?.length}
        error={error}
        isFetching={isLoading}
      />
    </Box>
  );
}
